import React, { memo, useEffect, useState } from "react"
import { Box, Flex, StackProps, Text } from "@chakra-ui/react"
import { graphql, useStaticQuery } from "gatsby"

import { useAppContext } from "@app/providers/app"
import { Swatch, useSwatch } from "@app/hooks/useSwatch"
import { Link } from "@app/components/Link"

import type { ProductProps, ProductCombinedProps } from "@root/types/global"

type Props = StackProps & {
  isLoading?: boolean
  product: ProductProps | ProductCombinedProps
  swatches?: Array<ProductProps>
  isReview?: boolean
}

export const ProductFormSwatches: React.FC<Props> = memo(({ isLoading = false, product, isReview, swatches: pdpSwatches, ...props }) => {
  const { product: locales } = useStaticQuery<GatsbyTypes.ProductFormAddToCartQuery>(graphql`
    query ProductFormSwatches {
      product: sanityTemplateProduct {
        additionalSwatchLabel
      }
    }
  `)

  const { activeVariant } = useAppContext()
  const { getProductFormSwatches } = useSwatch()

  const [swatches, setSwatches] = useState<Array<Swatch>>()

  useEffect(() => {
    if (pdpSwatches?.length) {
      setSwatches(getProductFormSwatches(pdpSwatches, activeVariant) as Array<Swatch>)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdpSwatches, activeVariant])

  const activeColour = swatches?.find(swatch => swatch.handle === product?.handle)?.name

  return (swatches?.length ?? 0) > 0 ? (
    <Box color="grey.text">
      <Text letterSpacing="generous" size="sm" mb={2}>
        {`${locales?.additionalSwatchLabel}: ${activeColour}`}
      </Text>
      <Flex
        alignItems="center"
        justifyContent="flex-start"
        opacity={isLoading ? 0.5 : 1}
        pointerEvents={isLoading ? "none" : "auto"}
        gap={3}
        wrap="wrap"
        {...props}
      >
        {swatches?.map(({ key, image, name, swatch, url, handle, isAvailable }) => {
          const active = handle === product?.handle
          if (isReview && !active) {
            return null
          }
          return (
            <Box key={key} pos="relative">
              <Flex
                as={!active ? Link : null}
                border="2px solid"
                borderRadius="full"
                borderColor={active ? "grey.coal" : isAvailable ? "transparent" : "grey.fog"}
                title={name}
                to={url}
                p={0.5}
                _hover={!active ? { opacity: 0.4 } : {}}
                _after={
                  !isAvailable
                    ? {
                        content: '""',
                        display: "block",
                        pos: "absolute",
                        top: "47.5%",
                        left: "2px",
                        w: "36px",
                        h: "2px",
                        bg: "grey.fog",
                        transform: "rotate(125deg)",
                      }
                    : {}
                }
              >
                <Flex borderRadius="full" pos="relative" overflow="hidden" h={8} w={8}>
                  <Box
                    pos="absolute"
                    pointerEvents="none"
                    inset={0}
                    bgColor={swatch?.colour || "grey.fog"}
                    bgImage={image ? `url(${image})` : undefined}
                    bgPosition="center"
                    bgSize="cover"
                  />
                </Flex>
              </Flex>
            </Box>
          )
        })}
      </Flex>
    </Box>
  ) : null
})
